import {
  collection,
  doc,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  deleteDoc,
  where,
  Timestamp,
  getDocs,
  getDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { notify } from "./notify";

const VISITOR_TABLE = "community_visitors";
const SECURITY_CODES_TABLE = "community_security_codes";

/* ═══════════════════════════════════════════════════════════════
 *  VISITOR MANAGEMENT
 *
 *  Shape:
 *  {
 *    communityCode,
 *    visitorName,
 *    phone,
 *    purpose,                    // free text
 *    category: "guest" | "delivery" | "cab" | "service" | "domestic_help",
 *    company,                    // company/org name for delivery & service
 *    flatNumber,                 // which flat the visitor is going to
 *    residentUserId,             // the resident who pre-approved / is being visited
 *    residentName,
 *    preApproved: true|false,    // was this pre-approved by resident?
 *    expectedDate: "2026-03-05", // ISO date string
 *    expectedTime: "14:00",      // HH:mm for expected arrival
 *    status: "expected" | "checked_in" | "checked_out" | "cancelled",
 *    entryCode: "1234",          // 4-digit verification code
 *    entryTime: Timestamp|null,
 *    exitTime: Timestamp|null,
 *    notes,
 *    recurring: false,           // for domestic help / frequent visitors
 *    vehicleNumber,              // optional
 *    createdBy,                  // userId who created the entry
 *    createdAt: Timestamp,
 *  }
 * ═══════════════════════════════════════════════════════════════ */

export const VISITOR_CATEGORIES = [
  { value: "guest", label: "Guest", icon: "🧑" },
  { value: "delivery", label: "Delivery", icon: "📦" },
  { value: "cab", label: "Cab / Ride", icon: "🚕" },
  { value: "service", label: "Service Person", icon: "🔧" },
  { value: "domestic_help", label: "Domestic Help", icon: "🏠" },
];

/* ── Generate 4-digit entry code ───────────────────── */
function generateEntryCode() {
  return String(Math.floor(1000 + Math.random() * 9000));
}

/* ── Get today's visitors (real-time) ──────────────── */
export const getVisitorsInRealTime = (communityCode, cb) => {
  return onSnapshot(
    query(
      collection(db, VISITOR_TABLE),
      where("communityCode", "==", communityCode)
    ),
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() || 0;
        const tb = b.createdAt?.toMillis?.() || 0;
        return tb - ta;
      });
      cb(items);
    },
    () => cb([])
  );
};

/* ── Get visitors for a specific resident (real-time) ─ */
export const getMyVisitorsInRealTime = (communityCode, residentUserId, cb) => {
  return onSnapshot(
    query(
      collection(db, VISITOR_TABLE),
      where("communityCode", "==", communityCode),
      where("residentUserId", "==", residentUserId)
    ),
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() || 0;
        const tb = b.createdAt?.toMillis?.() || 0;
        return tb - ta;
      });
      cb(items);
    },
    () => cb([])
  );
};

/* ── Pre-approve / add a visitor ───────────────────── */
export const addVisitor = async ({
  communityCode,
  visitorName,
  phone,
  purpose,
  category,
  company,
  flatNumber,
  residentUserId,
  residentName,
  expectedDate,
  expectedTime,
  notes,
  recurring,
  vehicleNumber,
  createdBy,
  photoURL,
}) => {
  const docRef = doc(collection(db, VISITOR_TABLE));
  await setDoc(docRef, {
    communityCode,
    visitorName: visitorName || "",
    phone: phone || "",
    purpose: purpose || "",
    category: category || "guest",
    company: company || "",
    flatNumber: flatNumber || "",
    residentUserId: residentUserId || "",
    residentName: residentName || "",
    preApproved: true,
    status: "expected",
    expectedDate: expectedDate || "",
    expectedTime: expectedTime || "",
    entryCode: generateEntryCode(),
    entryTime: null,
    exitTime: null,
    notes: notes || "",
    recurring: recurring || false,
    vehicleNumber: vehicleNumber || "",
    photoURL: photoURL || null,
    createdBy: createdBy || "",
    createdAt: Timestamp.now(),
  });
  if (residentUserId && residentUserId !== createdBy) {
    notify({
      recipientId: residentUserId,
      category: "visitors",
      title: "Visitor Pre-Approved",
      body: `${visitorName || "A visitor"} is expected on ${expectedDate || ""}`,
      data: { visitorId: docRef.id, visitorName },
    }).catch(() => {});
  }
  return docRef.id;
};

/* ── Check in a visitor ────────────────────────────── */
export const checkInVisitor = async (visitorId, visitor) => {
  await updateDoc(doc(db, VISITOR_TABLE, visitorId), {
    status: "checked_in",
    entryTime: Timestamp.now(),
  });
  if (visitor?.residentUserId) {
    notify({
      recipientId: visitor.residentUserId,
      category: "visitors",
      title: "Visitor Checked In",
      body: `${visitor.visitorName || "Your visitor"} has checked in`,
      data: { visitorId },
    }).catch(() => {});
  }
};

/* ── Check out a visitor ───────────────────────────── */
export const checkOutVisitor = async (visitorId, visitor) => {
  await updateDoc(doc(db, VISITOR_TABLE, visitorId), {
    status: "checked_out",
    exitTime: Timestamp.now(),
  });
  if (visitor?.residentUserId) {
    notify({
      recipientId: visitor.residentUserId,
      category: "visitors",
      title: "Visitor Checked Out",
      body: `${visitor.visitorName || "Your visitor"} has checked out`,
      data: { visitorId },
    }).catch(() => {});
  }
};

/* ── Cancel a visitor entry ────────────────────────── */
export const cancelVisitor = async (visitorId) => {
  return updateDoc(doc(db, VISITOR_TABLE, visitorId), {
    status: "cancelled",
  });
};

/* ── Update visitor fields ─────────────────────────── */
export const updateVisitor = async (visitorId, data) => {
  return updateDoc(doc(db, VISITOR_TABLE, visitorId), data);
};

/* ── Delete visitor record ─────────────────────────── */
export const deleteVisitor = async (visitorId) => {
  return deleteDoc(doc(db, VISITOR_TABLE, visitorId));
};

/* ═══════════════════════════════════════════════════════════════
 *  SECURITY GATE OPERATIONS
 * ═══════════════════════════════════════════════════════════════ */

/* ── Verify entry code → returns matching visitor or null ────── */
export const verifyEntryCode = async (communityCode, entryCode) => {
  const snap = await getDocs(
    query(
      collection(db, VISITOR_TABLE),
      where("communityCode", "==", communityCode),
      where("entryCode", "==", entryCode),
      where("status", "==", "expected")
    )
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
};

/* ── Log a walk-in (not pre-approved by resident) ─────────── */
export const addWalkInVisitor = async ({
  communityCode,
  visitorName,
  phone,
  purpose,
  category,
  company,
  flatNumber,
  vehicleNumber,
  notes,
  guardId,
  guardName,
  photoURL,
}) => {
  const docRef = doc(collection(db, VISITOR_TABLE));
  await setDoc(docRef, {
    communityCode,
    visitorName: visitorName || "",
    phone: phone || "",
    purpose: purpose || "",
    category: category || "guest",
    company: company || "",
    flatNumber: flatNumber || "",
    residentUserId: "",
    residentName: "",
    preApproved: false,
    status: "checked_in",
    expectedDate: "",
    expectedTime: "",
    entryCode: "",
    entryTime: Timestamp.now(),
    exitTime: null,
    notes: notes || "",
    recurring: false,
    vehicleNumber: vehicleNumber || "",
    photoURL: photoURL || null,
    createdBy: guardId || "",
    guardName: guardName || "",
    createdAt: Timestamp.now(),
  });
  return docRef.id;
};

/* ── Deny / reject a visitor at the gate ───────────────────── */
export const denyVisitor = async (visitorId, reason, visitor) => {
  await updateDoc(doc(db, VISITOR_TABLE, visitorId), {
    status: "denied",
    denyReason: reason || "",
  });
  if (visitor?.residentUserId) {
    notify({
      recipientId: visitor.residentUserId,
      category: "visitors",
      title: "Visitor Access Denied",
      body: `${visitor.visitorName || "A visitor"} was denied entry${reason ? `: ${reason}` : ""}`,
      data: { visitorId },
    }).catch(() => {});
  }
};

/* ═══════════════════════════════════════════════════════════════
 *  SECURITY ACCESS CODE MANAGEMENT
 *
 *  community_security_codes collection:
 *  {
 *    communityCode,
 *    pin: "123456",           // 6-digit access PIN
 *    label: "Main Gate Guard", // human-friendly name
 *    active: true,
 *    createdBy,               // userId of committee member who created it
 *    createdByName,
 *    createdAt: Timestamp,
 *  }
 * ═══════════════════════════════════════════════════════════════ */

/* ── Generate a random 6-digit PIN ───────────────── */
function generateSecurityPin() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

/* ── Create a new security access code ───────────── */
export const createSecurityCode = async ({ communityCode, label, createdBy, createdByName }) => {
  const pin = generateSecurityPin();
  const docRef = doc(collection(db, SECURITY_CODES_TABLE));
  await setDoc(docRef, {
    communityCode,
    pin,
    label: label || "Security Guard",
    active: true,
    createdBy: createdBy || "",
    createdByName: createdByName || "",
    createdAt: Timestamp.now(),
  });
  return { id: docRef.id, pin };
};

/* ── Validate a security PIN ────────────────────── */
export const validateSecurityPin = async (communityCode, pin) => {
  const snap = await getDocs(
    query(
      collection(db, SECURITY_CODES_TABLE),
      where("communityCode", "==", communityCode),
      where("pin", "==", pin),
      where("active", "==", true)
    )
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
};

/* ── Get all security codes for a community (real-time) ── */
export const getSecurityCodesInRealTime = (communityCode, cb) => {
  return onSnapshot(
    query(
      collection(db, SECURITY_CODES_TABLE),
      where("communityCode", "==", communityCode)
    ),
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      cb(items);
    },
    () => cb([])
  );
};

/* ── Toggle active/inactive ─────────────────────── */
export const toggleSecurityCode = async (codeId, active) => {
  return updateDoc(doc(db, SECURITY_CODES_TABLE, codeId), { active });
};

/* ── Delete a security code ─────────────────────── */
export const deleteSecurityCode = async (codeId) => {
  return deleteDoc(doc(db, SECURITY_CODES_TABLE, codeId));
};

/* ═══════════════════════════════════════════════════════════════
 *  AUTO-EXPIRE OLD VISITOR ENTRIES
 *
 *  Marks expected visitors as "expired" when their expectedDate
 *  is in the past (before today).
 * ═══════════════════════════════════════════════════════════════ */
export const autoExpireVisitors = async (communityCode) => {
  const today = new Date().toISOString().split("T")[0];
  const snap = await getDocs(
    query(
      collection(db, VISITOR_TABLE),
      where("communityCode", "==", communityCode),
      where("status", "==", "expected")
    )
  );
  const batch = writeBatch(db);
  let count = 0;
  snap.docs.forEach((d) => {
    const data = d.data();
    if (data.expectedDate && data.expectedDate < today) {
      batch.update(d.ref, { status: "expired" });
      count++;
    }
  });
  if (count > 0) await batch.commit();
  return count;
};

/* ═══════════════════════════════════════════════════════════════
 *  FREQUENT VISITORS
 *
 *  Residents can save visitors as "frequent" for quick re-invite.
 *
 *  community_frequent_visitors collection:
 *  {
 *    communityCode,
 *    userId,           // resident who saved it
 *    visitorName,
 *    phone,
 *    category,
 *    company,
 *    purpose,
 *    vehicleNumber,
 *    createdAt,
 *  }
 * ═══════════════════════════════════════════════════════════════ */
const FREQUENT_TABLE = "community_frequent_visitors";

export const saveFrequentVisitor = async (data) => {
  const docRef = doc(collection(db, FREQUENT_TABLE));
  await setDoc(docRef, {
    ...data,
    createdAt: Timestamp.now(),
  });
  return docRef.id;
};

export const getFrequentVisitorsOnce = async (communityCode, userId) => {
  const snap = await getDocs(
    query(
      collection(db, FREQUENT_TABLE),
      where("communityCode", "==", communityCode),
      where("userId", "==", userId)
    )
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const deleteFrequentVisitor = async (id) => {
  return deleteDoc(doc(db, FREQUENT_TABLE, id));
};

/* ═══════════════════════════════════════════════════════════════
 *  NOTIFICATIONS
 *
 *  community_notifications collection:
 *  {
 *    userId,          // target resident
 *    type,            // "visitor_checkin" etc.
 *    title,
 *    body,
 *    data: {},        // extra context (visitorId, flatNumber, etc.)
 *    read: false,
 *    createdAt,
 *  }
 * ═══════════════════════════════════════════════════════════════ */
const NOTIFICATION_TABLE = "community_notifications";

export const addNotification = async ({ userId, type, title, body, data }) => {
  const docRef = doc(collection(db, NOTIFICATION_TABLE));
  await setDoc(docRef, {
    userId,
    type: type || "visitor",
    title: title || "",
    body: body || "",
    data: data || {},
    read: false,
    createdAt: Timestamp.now(),
  });
  return docRef.id;
};

export const getNotificationsInRealTime = (userId, cb) => {
  return onSnapshot(
    query(
      collection(db, NOTIFICATION_TABLE),
      where("userId", "==", userId)
    ),
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      cb(items);
    },
    () => cb([])
  );
};

export const markNotificationRead = async (id) => {
  return updateDoc(doc(db, NOTIFICATION_TABLE, id), { read: true });
};

export const markAllNotificationsRead = async (ids) => {
  const batch = writeBatch(db);
  ids.forEach((id) => batch.update(doc(db, NOTIFICATION_TABLE, id), { read: true }));
  return batch.commit();
};
